import type {CmLogger} from "@cm/log"
import type {ImageTilesDescriptor} from "#utils/tiled-image/tiled-image-types"
import type {TiledImageBase} from "#utils/tiled-image/tiled-image-base"
import {isPromise} from "#utils/misc"
import {TiledImageDataCache, type TileDownloadFn} from "#utils/tiled-image/tiled-image-data-cache"

export class TiledImageManagerBase<TiledImageType extends TiledImageBase<unknown>> {
    constructor(
        private tileImageFactory: (descriptor: ImageTilesDescriptor, manager: TiledImageManagerBase<TiledImageType>) => Promise<TiledImageType>,
        readonly cacheInMemory: boolean,
        logger: CmLogger,
        downloadFn?: TileDownloadFn,
    ) {
        this.dataCache = new TiledImageDataCache(logger, cacheInMemory, downloadFn)
    }

    async createTiledImage(imageTilesDescriptor: ImageTilesDescriptor): Promise<TiledImageType> {
        let imageTileData = this._imageTileDataByIndexDownloadUrl.get(imageTilesDescriptor.tileDownloadUrlPrefix)
        if (!imageTileData) {
            const promisedImageTileData = this.tileImageFactory(imageTilesDescriptor, this)
            this._imageTileDataByIndexDownloadUrl.set(imageTilesDescriptor.tileDownloadUrlPrefix, promisedImageTileData)
            try {
                imageTileData = await promisedImageTileData
            } catch (error) {
                this._imageTileDataByIndexDownloadUrl.delete(imageTilesDescriptor.tileDownloadUrlPrefix)
                throw error
            }
            this._imageTileDataByIndexDownloadUrl.set(imageTilesDescriptor.tileDownloadUrlPrefix, imageTileData)
        } else if (isPromise(imageTileData)) {
            imageTileData = await imageTileData
        }
        return imageTileData
    }

    readonly dataCache: TiledImageDataCache

    private _imageTileDataByIndexDownloadUrl = new Map<string, TiledImageType | Promise<TiledImageType>>()
}
